import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Grid } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import { compose } from 'recompose';

import {
  ClientDepartment,
  ClientDepartmentStatus,
  ClientLocation,
  ClientLocationStatus,
  CreateClientDepartmentDocument,
  CreateClientLocationDocument,
  UpdateClientDepartmentDocument,
  UpdateClientLocationDocument,
} from 'api';
import {
  clientDepartmentCreateResponse,
  clientDepartmentUpdateResponse,
  clientLocationCreateResponse,
  clientLocationUpdateResponse,
} from 'apollo/responses';
import {
  createDepartment,
  createLocation,
  deleteDepartment,
  deleteLocation,
} from 'apollo/reducers';
import { EditableList, EditableListType } from 'features/admin/types';
import { useNotify } from 'hooks';

import Create from './Create';
import Update from './Update';

interface IProps {
  list: Array<ClientDepartment | ClientLocation>;
  listType: EditableList;
}

interface IPropsEnhanced extends IProps, WithStyles<typeof styles>, RouteComponentProps {}

const styles = (theme: Theme) =>
  createStyles({
    spacer: {
      height: theme.spacing(2),
    },
  });

const enhancers = compose<IPropsEnhanced, IProps>(withRouter, withStyles(styles));

const DepartmentsLocationsIndex: FC<IPropsEnhanced> = ({ classes, list, listType }) => {
  const { t } = useTranslation();
  const { info } = useNotify();

  const isDepartments = listType === EditableListType.Departments;
  const options = isDepartments ? getDepartmentsOptions() : getLocationsOptions();

  function getDepartmentsOptions() {
    return {
      createMutation: CreateClientDepartmentDocument,
      updateMutation: UpdateClientDepartmentDocument,
      createResponse: clientDepartmentCreateResponse,
      updateResponse: clientDepartmentUpdateResponse,
      createReducer: createDepartment,
      deleteReducer: deleteDepartment,
      activeStatus: { department_status: ClientDepartmentStatus.Active },
      disabledStatus: { department_status: ClientDepartmentStatus.Disabled },
    };
  }

  function getLocationsOptions() {
    return {
      createMutation: CreateClientLocationDocument,
      updateMutation: UpdateClientLocationDocument,
      createResponse: clientLocationCreateResponse,
      updateResponse: clientLocationUpdateResponse,
      createReducer: createLocation,
      deleteReducer: deleteLocation,
      activeStatus: { location_status: ClientLocationStatus.Active },
      disabledStatus: { location_status: ClientLocationStatus.Disabled },
    };
  }

  function notify() {
    const message = t('components:savedSuccessfully');
    info({ message });
  }

  return (
    <Fragment>
      <Create
        titleKey={`admin:${listType}`}
        inputPlaceholderKey={`admin:${listType}AddPlaceholder`}
        onCompleted={notify}
        {...options}
      />
      <div className={classes.spacer} />
      <Grid container alignItems="center" spacing={3}>
        <Grid item xs={3} />
        <Grid item xs={7}>
          <Grid container direction="column" spacing={2}>
            {list.map(listItem => (
              <Update
                key={listItem.id}
                item={listItem}
                inputPlaceholderKey={`admin:${listType}AddPlaceholder`}
                confirmDeleteKey={`admin:${listType}DeleteAlert`}
                onCompleted={notify}
                {...options}
              />
            ))}
          </Grid>
        </Grid>
        <Grid item xs={2} />
      </Grid>
    </Fragment>
  );
};

export default enhancers(DepartmentsLocationsIndex);
